import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadOffIcon from '@mui/icons-material/FileDownloadOff';
import { Link as RouterLink } from 'react-router-dom';

import { AccountsPayableItem } from './types';
import { hasPartialRemit, remitFilesOf } from './utils';
import { StatusBadge } from './StatusBadge';
import { ToggleStatusPill } from './ToggleStatusPill';

export type APCardProps = {
  rowId: string;
  week: string;
  index: number;
  item: AccountsPayableItem;
  onRequestToggleRemit: (rowId: string, index: number, currentValue: boolean) => void;
};

const META_KEY_SX = {
  color: 'text.disabled',
  fontSize: 10.5,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
};

const META_VALUE_SX = {
  color: 'text.primary',
  fontSize: 13,
  fontWeight: 500,
};

const META_EMPTY_SX = {
  ...META_VALUE_SX,
  color: 'text.disabled',
  fontStyle: 'italic',
  fontWeight: 400,
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      color: 'text.disabled',
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
    }}
  >
    {children}
  </Typography>
);

const downloadIconBtnSx = {
  width: 32,
  height: 32,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'grey.300',
  color: 'primary.main',
  backgroundColor: 'background.paper',
  transition: 'all 0.12s ease',
  '&:hover': {
    backgroundColor: 'primary.light',
    borderColor: 'primary.main',
  },
  '&.Mui-disabled': {
    color: 'text.disabled',
    backgroundColor: 'grey.50',
    borderColor: 'divider',
  },
};

export const APCard = ({
  rowId,
  week,
  index,
  item,
  onRequestToggleRemit,
}: APCardProps) => {
  const paymentProvided = item.APRemitInformationProvided === true;
  const partialAvailable = hasPartialRemit(item);
  const remitFiles = remitFilesOf(item);

  // auto generated file remit file, no input required
  const isGenerated = Boolean(item.RemittanceFiles && item.RemittanceFiles.length > 0);

  const applyHref = `/parcel/${rowId}?apRemitFile=${encodeURIComponent(item.AccountsPayableRemitFile || '')}&week=${encodeURIComponent(week || '')}&apIndex=${index}`;

  return (
    <Box
      sx={{
        background: 'background.paper',
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* HEAD */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={META_KEY_SX}>EDI Account</Typography>
          {item.EDIAccountNumber ? (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 15,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {item.EDIAccountNumber}
            </Typography>
          ) : (
            <Typography sx={{ ...META_EMPTY_SX, fontSize: 13 }}>—</Typography>
          )}
        </Box>

        {isGenerated ? null : paymentProvided ? (
          <Tooltip title="Settlement info already provided" arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled
                startIcon={<CheckIcon />}
                sx={{
                  borderRadius: 1.25,
                  borderColor: 'divider',
                  color: 'text.disabled',
                  '&.Mui-disabled': {
                    backgroundColor: 'grey.50',
                    color: 'text.disabled',
                    borderColor: 'divider',
                  },
                }}
              >
                Settlement Info Added
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Enter ACH # and settlement date for this account" arrow>
            <Button
              component={RouterLink}
              to={applyHref}
              variant="contained"
              size="small"
              startIcon={<EditNoteIcon />}
              sx={{ borderRadius: 1.25 }}
            >
              Add Settlement Info
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* META */}
      <Box
        sx={{
          display: isGenerated ? 'none' : 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
          pt: 1.5,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography sx={META_KEY_SX}>ACH #</Typography>
          <Typography sx={item.FedExACHNumber ? META_VALUE_SX : META_EMPTY_SX}>
            {item.FedExACHNumber || 'Not yet received'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={META_KEY_SX}>Settlement Date</Typography>
          <Typography sx={item.FedExSettlementDate ? META_VALUE_SX : META_EMPTY_SX}>
            {item.FedExSettlementDate || '—'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={META_KEY_SX}>Settlement Info</Typography>
          <Box sx={{ mt: 0.25 }}>
            <StatusBadge
              tone={paymentProvided ? 'complete' : 'pending'}
              label={paymentProvided ? 'Provided' : 'Not provided'}
              size="sm"
            />
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          pt: 1.5,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        {isGenerated ? (
          <>
            <SectionLabel>
              {remitFiles.length === 1 ? 'Remit File' : `Remit Files · ${remitFiles.length} parts`}
            </SectionLabel>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              {remitFiles.map((file) => (
                <Tooltip key={file.Part} title={`Download final remittance file, part ${file.Part}`} arrow>
                  <Button
                    component="a"
                    href={file.Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    sx={{ borderRadius: 1.25, borderColor: 'grey.300' }}
                  >
                    {remitFiles.length === 1 ? 'Download' : `Part ${file.Part}`}
                  </Button>
                </Tooltip>
              ))}
            </Box>
          </>
        ) : (
          <>
            <SectionLabel>Remit Files</SectionLabel>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Tooltip
                title={partialAvailable ? 'Download partial remit file' : 'Partial remit not yet available'}
                arrow
              >
                <span>
                  {partialAvailable ? (
                    <IconButton
                      size="small"
                      component="a"
                      href={item.AccountsPayableRemitFile as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download partial remit file"
                      sx={downloadIconBtnSx}
                    >
                      <DownloadIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      disabled
                      aria-label="Partial remit not available"
                      sx={downloadIconBtnSx}
                    >
                      <FileDownloadOffIcon />
                    </IconButton>
                  )}
                </span>
              </Tooltip>

              <Tooltip
                title={
                  item.RemittanceFileURL
                    ? 'Download final remittance file'
                    : 'Final remit unavailable until ACH # and settlement date are provided'
                }
                arrow
              >
                <span>
                  {item.RemittanceFileURL ? (
                    <IconButton
                      size="small"
                      component="a"
                      href={item.RemittanceFileURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download final remittance file"
                      sx={downloadIconBtnSx}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      disabled
                      aria-label="Final remittance file not available"
                      sx={downloadIconBtnSx}
                    >
                      <FileDownloadOffIcon />
                    </IconButton>
                  )}
                </span>
              </Tooltip>

              <ToggleStatusPill
                value={item.RemittanceFileUploaded === true}
                onClick={() =>
                  onRequestToggleRemit(rowId, index, item.RemittanceFileUploaded === true)
                }
                ariaLabel={
                  item.RemittanceFileUploaded
                    ? 'Remit uploaded — click to mark as not uploaded'
                    : 'Remit not uploaded — click to mark as uploaded'
                }
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
